import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { PostMessageService } from '@services/post-message';
import { PreferencesService, ReportsService } from '@shared/services';
import { IEvent, IEventOrderDateRange, IEventPeriodItem } from '@shared/types/event.type';

import {
    get_events,
    get_events_failure,
    get_events_success,
    SHOW_EVENT_SELECTOR,
    show_event_selector,
    show_event_selector_finish,
    update_event_selection,
    update_event_selection_failure,
    update_event_selection_success,
} from '../actions/events.action';

@Injectable()
export class EventsEffects {
    showEventsSelector$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(show_event_selector),
            tap(({ options, msgOptions }) => {
                this.postMessageService.sendMessage(SHOW_EVENT_SELECTOR, msgOptions.messageId, options);
            }),
            map(() => show_event_selector_finish()),
        );
    });

    updateEventSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(update_event_selection),
            map(({ events, isMultiple, reportId }) => update_event_selection_success({ events, isMultiple, reportId })),
            catchError(async () => update_event_selection_failure),
        );
    });

    getEvents$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_events),
            mergeMap(({ companyId, dataFilter, defaultSort }) => {
                const hasActiveEvents = this.preferences.companyHasActiveEvents;
                let posts = [];
                if (hasActiveEvents) {
                    posts = [
                        this.reportsService.fetchEvents(companyId, dataFilter, defaultSort),
                        this.reportsService.fetchEventsOrderDates(),
                    ];
                } else {
                    posts = [of([]), of({ eventsOrderDateRange: null, originalEventsOrderDateRange: null })];
                }
                return forkJoin([...posts]).pipe(
                    tap(
                        ([events, eventPeriodRes]: [
                            IEvent[],
                            {
                                eventsOrderDateRange: Record<string, IEventOrderDateRange>;
                                originalEventsOrderDateRange: IEventPeriodItem[];
                            },
                        ]) => {
                            events.forEach((event) => {
                                const { order_start_date, order_end_date } =
                                    eventPeriodRes.eventsOrderDateRange[event.id] || {};

                                event.order_dates = order_start_date ? [order_start_date, order_end_date] : [];
                            });
                        },
                    ),
                    map(
                        ([events, eventPeriodRes]: [
                            IEvent[],
                            {
                                eventsOrderDateRange: Record<string, IEventOrderDateRange>;
                                originalEventsOrderDateRange: IEventPeriodItem[];
                            },
                        ]) => {
                            return get_events_success({
                                events,
                                eventsOrderDateRange: eventPeriodRes.eventsOrderDateRange,
                                originalEventPeriodData: eventPeriodRes.originalEventsOrderDateRange,
                            });
                        },
                    ),
                    catchError(async () => get_events_failure),
                );
            }),
        );
    });

    constructor(
        private postMessageService: PostMessageService,
        private actions$: Actions,
        private reportsService: ReportsService,
        private preferences: PreferencesService,
    ) {}
}
